import {
    styled,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { ficheInscriptionEntryType } from '../../../lib/api/api';
import { paymentModeType } from '../types';
import { convertToFrenchDate } from '../../../lib/utils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconButton } from '../../../components/IconButton';
import { useNavigate } from 'react-router-dom';
import { pathHandler } from '../../../lib/pathHandler';
import { useState } from 'react';
import { useAlert } from '../../../lib/alert';

const paymentModeLabelMapping: Record<paymentModeType, string> = {
    'comptant-cheque': 'Comptant / chèques',
    'comptant-virement': 'Comptant / virement',
    'echelonne-cheque': 'Échelonné / chèques',
    'echelonne-virement': 'Échelonné / virements',
};
type orderByColumnType = 'created_at' | 'firstName' | 'lastName' | 'selectedPromo';

type orderByType = { column: orderByColumnType; direction: 'asc' | 'desc' };

function FicheInscriptionTable(props: { ficheInscriptionEntries: ficheInscriptionEntryType[] }) {
    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState<orderByType | undefined>({
        column: 'created_at',
        direction: 'desc',
    });
    const { displayAlert } = useAlert();

    const sortedFormationInitialeEntries = sortData(props.ficheInscriptionEntries);

    return (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'created_at'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('created_at')}
                        >
                            Date de réception
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'firstName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('firstName')}
                        >
                            Prénom
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'lastName'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('lastName')}
                        >
                            NOM
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Adresse e-mail</TableCell>
                    <TableCell>
                        <TableSortLabel
                            active={orderBy?.column === 'selectedPromo'}
                            direction={orderBy?.direction}
                            onClick={buildSortHandler('selectedPromo')}
                        >
                            Année inscription
                        </TableSortLabel>
                    </TableCell>
                    <TableCell>Première année d'inscription</TableCell>
                    <TableCell>Mode de paiement</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sortedFormationInitialeEntries.map((ficheInscriptionEntry) => {
                    const rowData = computeRowData(ficheInscriptionEntry);
                    return (
                        <TableRow key={ficheInscriptionEntry.id}>
                            {rowData.map((cellData, index) => (
                                <TableCell key={index}>{cellData}</TableCell>
                            ))}
                            <TableCell>
                                <CellRow>
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Copier la ligne"
                                        IconComponent={ContentCopyIcon}
                                        onClick={() => copyRowToClipboard(rowData)}
                                    />
                                    <IconButton
                                        color="primary"
                                        size="small"
                                        title="Aller au formulaire"
                                        IconComponent={ArrowForwardIcon}
                                        onClick={() =>
                                            navigateToEntryPreview(ficheInscriptionEntry.id)
                                        }
                                    />
                                </CellRow>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );

    function sortData(data: ficheInscriptionEntryType[]) {
        if (orderBy === undefined) {
            return data;
        }
        return data.sort((a, b) => {
            const aValue = a[orderBy.column];
            const bValue = b[orderBy.column];
            if (aValue === bValue) {
                return 0;
            }
            const comparison = aValue.localeCompare(bValue);

            return orderBy.direction === 'asc' ? comparison : -comparison;
        });
    }

    function buildSortHandler(orderByColumn: orderByColumnType) {
        return () => {
            if (orderBy === undefined || orderBy.column !== orderByColumn) {
                setOrderBy({ column: orderByColumn, direction: 'asc' });
                return;
            } else {
                switch (orderBy.direction) {
                    case 'asc':
                        setOrderBy({ column: orderByColumn, direction: 'desc' });
                        break;
                    case 'desc':
                        setOrderBy({ column: orderByColumn, direction: 'asc' });
                        break;
                }
            }
        };
    }

    function computeRowData(ficheInscriptionEntry: ficheInscriptionEntryType): string[] {
        const readableFirstInscriptionYear = computeReadableFirstInscriptionYear(
            ficheInscriptionEntry.firstInscriptionYear,
        );
        const rowData = [
            convertToFrenchDate(ficheInscriptionEntry.created_at.substring(0, 10)),
            ficheInscriptionEntry.firstName.toUpperCase(),
            ficheInscriptionEntry.lastName.toUpperCase(),
            ficheInscriptionEntry.email,
            ficheInscriptionEntry.selectedPromo,
            readableFirstInscriptionYear,
            paymentModeLabelMapping[ficheInscriptionEntry.selectedPaymentMode],
        ];
        return rowData;
    }

    function navigateToEntryPreview(ficheInscriptionId: string) {
        const path = pathHandler.getRoutePath(
            'FICHE_INSCRIPTION_PREVIEW',
            {
                ficheInscriptionId,
            },
            { role: 'admin' },
        );
        navigate(path);
    }

    async function copyRowToClipboard(rowData: string[]) {
        const textToCopy = rowData.join('\t'); // Use tab as a separator for
        try {
            await navigator.clipboard.writeText(textToCopy);
            displayAlert({
                variant: 'success',
                text: 'Ligne copiée dans le presse-papiers.',
            });
        } catch (error) {
            displayAlert({
                variant: 'error',
                text: `Échec de la copie dans le presse-papiers: ${error}`,
            });
        }
    }
}

function computeReadableFirstInscriptionYear(
    firstInscriptionYear: ficheInscriptionEntryType['firstInscriptionYear'],
): string {
    if (firstInscriptionYear === null) {
        return '-';
    }
    if (firstInscriptionYear === 'before2023') {
        return 'Avant 2023';
    }
    return firstInscriptionYear;
}

const CellRow = styled('div')(({ theme }) => ({
    display: 'flex',
}));

export { FicheInscriptionTable };
